import React, { useState, useEffect, useRef } from 'react';
import MarathiTable from './MarathiTable';
import './PrintTestPage.css';

// ─── Detect device/mode ───────────────────────────────────────────────────────
function getDeviceInfo() {
    const ua = navigator.userAgent;
    const isAndroid = /android/i.test(ua);
    const isWindows = /windows/i.test(ua);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true; // fallback (iOS only, but harmless)
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(ua);
    const isTWA = document.referrer.startsWith('android-app://');

    let platform = 'Unknown';
    if (isAndroid) platform = '🤖 Android';
    else if (isWindows) platform = '🪟 Windows';

    let mode = isStandalone ? '📱 Standalone PWA' : '🌐 Browser Tab';
    if (isTWA) mode += ' (TWA)';

    return { platform, mode, isStandalone, isAndroid, isMobile, ua };
}

// ─── Print date/time ──────────────────────────────────────────────────────────
function getMarathiDateTime() {
    const now = new Date();
    const d = now.toLocaleDateString('mr-IN', {
        day: '2-digit', month: 'long', year: 'numeric',
    });
    const t = now.toLocaleTimeString('mr-IN', {
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
    return `${d} | ${t}`;
}

export default function PrintTestPage() {
    const [deviceInfo] = useState(getDeviceInfo);
    const [dateTime] = useState(getMarathiDateTime);
    const [printLog, setPrintLog] = useState([]);
    const [isPrinting, setIsPrinting] = useState(false);
    const logRef = useRef([]);

    // ─── Log helper ─────────────────────────────────────────────────────────────
    const addLog = (msg, type = 'info') => {
        const entry = {
            id: Date.now() + Math.random(),
            time: new Date().toLocaleTimeString(),
            msg,
            type,
        };
        logRef.current = [entry, ...logRef.current].slice(0, 20);
        setPrintLog([...logRef.current]);
        console.log(`[PrintTest] [${type.toUpperCase()}] ${msg}`);
    };

    // ─── Event listeners ────────────────────────────────────────────────────────
    useEffect(() => {
        addLog('App loaded. Device: ' + deviceInfo.platform + ' | Mode: ' + deviceInfo.mode, 'info');

        if (deviceInfo.isStandalone) {
            addLog('⚠️ Running as Standalone PWA — window.print() may be silently blocked on Android!', 'warn');
        }

        const handleBeforePrint = () => {
            addLog('✅ beforeprint event fired!', 'success');
            setIsPrinting(true);
        };

        const handleAfterPrint = () => {
            addLog('✅ afterprint event fired — print dialog was shown/closed.', 'success');
            setIsPrinting(false);
        };

        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);

        // matchMedia approach (some browsers use this instead of events)
        const mediaQueryList = window.matchMedia('print');
        const handleMediaChange = (e) => {
            if (e.matches) {
                addLog('✅ matchMedia(print) = true — print layout is active.', 'success');
            } else {
                addLog('matchMedia(print) = false — returned to screen.', 'info');
            }
        };
        if (mediaQueryList.addEventListener) {
            mediaQueryList.addEventListener('change', handleMediaChange);
        } else {
            // older browsers
            mediaQueryList.addListener(handleMediaChange);
        }

        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
            if (mediaQueryList.removeEventListener) {
                mediaQueryList.removeEventListener('change', handleMediaChange);
            } else {
                mediaQueryList.removeListener(handleMediaChange);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Auto-print on fallback open ────────────────────────────────────────────
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('autoprint') === 'true') {
            addLog('🔄 Auto-print triggered from URL parameter.', 'info');
            const timer = setTimeout(() => {
                window.print();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    // ─── Print handler ──────────────────────────────────────────────────────────
    const handlePrint = () => {
        addLog('🖨️ Print button clicked...', 'info');

        // Android PWA Print Bug Workaround
        if (deviceInfo.isAndroid && deviceInfo.isStandalone) {
            addLog('⚠️ Android PWA detected. Bypassing potential Print Spooler freeze by opening in a normal browser tab.', 'warn');
            alert('To ensure printing works correctly without getting stuck, the report will open in your default browser.');
            const url = new URL(window.location.href);
            url.searchParams.set('autoprint', 'true');
            window.open(url.toString(), '_blank');
            return;
        }

        try {
            window.print();
            // Note: if beforeprint never fires after this, the call was silently blocked.
            setTimeout(() => {
                // Check if beforeprint fired recently
                const recentFire = logRef.current.find((l) => l.msg.includes('beforeprint event fired'));
                // If the most recent beforeprint was more than 3 seconds ago, it means the current print() was blocked.
                if (!recentFire || Date.now() - recentFire.id > 3000) {
                    addLog(
                        '❌ Print prompt did not appear. The browser might have blocked it silently.',
                        'error'
                    );
                }
            }, 3000);
        } catch (err) {
            addLog('❌ window.print() threw an error: ' + err.message, 'error');
        }
    };

    // ─── Fallback: open in new browser tab ──────────────────────────────────────
    const handleOpenInBrowser = () => {
        addLog('Opening page in new browser tab as fallback...', 'info');
        const url = new URL(window.location.href);
        url.searchParams.set('autoprint', 'true');
        window.open(url.toString(), '_blank');
    };

    return (
        <div>
            {/* ── Screen Layout (normal UI) ── */}
            <div className="screen-view ptp-page">
                {/* ── Header ── */}
                <header className="ptp-header">
                    <div className="ptp-header-badge">
                        <span className="badge-dot pulse" />
                        Print Debug Tool
                    </div>
                    <h1 className="ptp-title">
                        <span className="title-marathi">दूध संकलन नोंदी</span>
                        <span className="title-sub">— Print Test</span>
                    </h1>
                    <p className="ptp-subtitle">
                        window.print() functionality test for Desktop &amp; Android PWA
                    </p>
                </header>

                {/* ── Device Info Card ── */}
                <div className="info-grid">
                    <div className="info-card">
                        <span className="info-label">Platform</span>
                        <span className="info-value">{deviceInfo.platform}</span>
                    </div>
                    <div className="info-card">
                        <span className="info-label">Display Mode</span>
                        <span className={`info-value ${deviceInfo.isStandalone ? 'warn' : 'good'}`}>
                            {deviceInfo.mode}
                        </span>
                    </div>
                    <div className="info-card">
                        <span className="info-label">Mobile</span>
                        <span className="info-value">{deviceInfo.isMobile ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="info-card">
                        <span className="info-label">Print Status</span>
                        <span className={`info-value ${isPrinting ? 'good pulse-text' : ''}`}>
                            {isPrinting ? '🖨️ Printing...' : 'Idle'}
                        </span>
                    </div>
                </div>

                {/* ── Android PWA Warning ── */}
                {deviceInfo.isStandalone && deviceInfo.isAndroid && (
                    <div className="pwa-warning-banner">
                        <div className="banner-icon">⚠️</div>
                        <div className="banner-content">
                            <strong>Android Standalone PWA Detected</strong>
                            <p>
                                <code>window.print()</code> may be silently blocked in Android Chrome standalone mode.
                                If no print dialog opens, use the <strong>"Open in Browser"</strong> fallback below.
                            </p>
                        </div>
                    </div>
                )}

                {/* ── Action Buttons ── */}
                <div className="action-section">
                    <button className="btn-print" onClick={handlePrint}>
                        <span className="btn-icon">🖨️</span>
                        Print करा
                    </button>

                    {deviceInfo.isStandalone && (
                        <button className="btn-fallback" onClick={handleOpenInBrowser}>
                            <span className="btn-icon">🌐</span>
                            Open in Browser (Fallback)
                        </button>
                    )}
                </div>

                {/* ── Print Area preview on screen ── */}
                <div className="print-area-label">👇 खालील तक्ता Print मध्ये दिसेल</div>

                <div className="screen-print-preview">
                    <h2>दूध संकलन नोंद — मराठी तक्ता</h2>
                    <p className="print-meta">
                        दिनांक: {dateTime} | एकूण शेतकरी: 120
                    </p>
                    <MarathiTable />
                    <p className="print-footer">
                        हे रेकॉर्ड संगणक प्रणालीद्वारे तयार केले आहे. — Print Test App
                    </p>
                </div>

                {/* ── Debug Log Panel ── */}
                <div className="debug-panel">
                    <div className="debug-header">
                        <span>🔍 Debug Log</span>
                        <button
                            className="btn-clear-log"
                            onClick={() => { logRef.current = []; setPrintLog([]); }}
                        >
                            Clear
                        </button>
                    </div>
                    <div className="debug-explanation">
                        <strong>How to diagnose:</strong> Click "Print करा" → watch if{' '}
                        <code>beforeprint</code> fires below. If it does, print dialog was shown.
                        If not, <code>window.print()</code> was silently blocked by the PWA shell.
                    </div>
                    <div className="debug-log-list">
                        {printLog.length === 0 && (
                            <div className="log-entry info">Waiting for events...</div>
                        )}
                        {printLog.map((entry) => (
                            <div key={entry.id} className={`log-entry ${entry.type}`}>
                                <span className="log-time">{entry.time}</span>
                                <span className="log-msg">{entry.msg}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── User Agent ── */}
                <div className="ua-strip">
                    <strong>UA:</strong> {deviceInfo.ua}
                </div>
            </div>

            {/* ── Hidden Print Layout (A4) ── */}
            <div className="print-view">
                <div className="a4-page">
                    <h2>दूध संकलन नोंद — मराठी तक्ता</h2>
                    <p className="print-meta">
                        दिनांक: {dateTime} | एकूण शेतकरी: 120
                    </p>
                    <MarathiTable />
                    <p className="print-footer">
                        हे रेकॉर्ड संगणक प्रणालीद्वारे तयार केले आहे. — Print Test App
                    </p>
                </div>
            </div>
        </div>
    );
}
